import ObjectUtils from '@spothero/utils/object';
import {spotheroPost} from 'api';
import {convertKeysToSnakeCase} from 'utils/format-strings';

/**
 * Formats the request to get searched facilities.
 * @function formatGetSearchedFacilitiesRequest
 * @param request
 * @return {Dictionary<{}>|Dictionary<Object[keyof Object]>|*}
 *
 * @memberof module:api/facilities/dto
 */
const formatGetSearchedFacilitiesRequest = (request) => {
    const formattedCities = request?.filters?.cities?.map((item) => {
        const city = item.value.split(',')[0].trim();
        const state = item.value.split(',')[1].trim();

        return {
            city,
            state,
        };
    });

    const formattedStatuses = request?.filters?.status?.map(
        (item) => item.value
    );

    let formattedRequest = {
        ...request,
        limit: request?.limit || 25,
        offset: request?.offset || 0,
        filters: {
            cities: formattedCities,
            status: formattedStatuses,
        },
        facilityIds: request?.facilityIds?.toString() || '',
        operatorEmail: request?.filters?.operatorEmail || '',
    };

    formattedRequest = convertKeysToSnakeCase(formattedRequest);

    return formattedRequest;
};
/**
 * Format the response of searched facilities.
 *
 * @function formatGetSearchedFacilitiesResponse
 * @param {Object} data - The data to be formatted.
 * @returns {Object} - The formatted response object.
 *
 * @memberof module:api/facilities/dto
 */
const formatGetSearchedFacilitiesResponse = (data) => {
    const formattedResponse = ObjectUtils.camelizeKeys(data);

    const canonicalFacilities =
        formattedResponse?.data?.data?.results?.canonicalFacilities;

    const formattedCanonicalFacilities = canonicalFacilities?.reduce(
        (acc, facility) => {
            const formattedFacility = {
                ...facility,
                parkingSpots: facility?.parkingSpots?.map((parkingSpot) => {
                    return {
                        ...parkingSpot,
                        address: facility?.address,
                    };
                }),
            };

            return [...acc, formattedFacility];
        },
        []
    );

    const response = {
        results: formattedCanonicalFacilities,
        meta: formattedResponse?.data?.meta,
    };

    return response;
};

/**
 * Retrieves searched facilities from the operator API based on the provided search parameters.
 *
 * @param {object} params - The search parameters for retrieving facilities.
 * @returns {Promise<FacilitiesSearchResponse>} - A promise that resolves with the formatted response containing the searched facilities.
 * @throws {Error} - If there is an error retrieving the searched facilities.
 *
 * @see '/api/v1/facilities/operator-facility-search/'
 */
const getSearchedFacilities = async (params) => {
    try {
        const formattedRequest = formatGetSearchedFacilitiesRequest(params);

        const response = await spotheroPost(
            '/facilities/operator-facility-search/',
            formattedRequest,
            {},
            false
        );

        const formattedResponse = formatGetSearchedFacilitiesResponse(response);

        return formattedResponse;
    } catch (error) {
        throw Error(error);
    }
};

export default getSearchedFacilities;
